// frontend/src/components/LOS/Niyogin/NiyoginInitiateDigilocker.tsx
//
// Flow:
//  1. User sees page with "Initiate DigiLocker KYC" button
//  2. User clicks → API fires → backend returns digilocker_url (only if Niyogin HTTP 200)
//  3. DigiLocker opens in a NEW TAB — the loan application tab stays open
//  4. User completes KYC on Digitap's page ("Documents verified ✅")
//  5. User switches back here and clicks "I've Completed KYC — Continue"
//  6. Frontend calls the confirm API (digilocker_return=true)
//     → backend saves Aadhaar image + KYC data from Niyogin response
//  7. Frontend polls /api/trigger_niyogin_digilocker_status/ every 3s
//     until has_aadhaar_image=true (or max 10 attempts)
//  8. onDone() advances the journey
//
// AUTO path: if Niyogin redirects back with ?digilocker_return=1, the
//   countdown + auto-confirm fires instead of the manual button.
//
// On any Niyogin non-200 response the error message is shown and the user stays here.

import React, { useEffect, useRef, useState } from "react";
import axios from "@/lib/axios";
import { API_BASE_URL } from "@/config";
import JourneyHeader from "@/components/JourneyHeader";

interface Props {
  value?: any;
  onChange?: (val: any) => void;
  disabled?: boolean;
  registerSubmit?: (handler: () => Promise<boolean>) => void;
  onDone?: () => void;
}

const RETURN_PARAM = "digilocker_return";
const POLL_INTERVAL_MS = 3000;
const MAX_POLL_ATTEMPTS = 15; // 15 × 3s = 45s max wait

const NiyoginInitiateDigilocker: React.FC<Props> = ({ onDone }) => {
  const calledRef = useRef(false);

  // Detect if we're back from DigiLocker via URL param redirect
  const isReturn = new URLSearchParams(window.location.search).get(RETURN_PARAM) === "1";

  const [phase, setPhase] = useState<
    | "idle"
    | "calling"
    | "awaiting_user"
    | "waiting"      // auto-return countdown
    | "confirming"   // calling confirm API
    | "polling"      // polling for image
    | "done"
    | "error"
  >(isReturn ? "waiting" : "idle");

  const [countdown, setCountdown] = useState(5);
  const [pollAttempt, setPollAttempt] = useState(0);
  const [error, setError] = useState<string | null>(null);
  const [kycName, setKycName] = useState<string | null>(null);
  // Store the digilocker URL so we can show a "reopen" link
  const [digilockerUrl, setDigilockerUrl] = useState<string | null>(null);

  // ── Helper: call the backend confirm/initiate API ─────────────────────────
  const callApi = async (opts: { digilocker_return?: boolean; redirect_url?: string } = {}) => {
    const res = await axios.post(
      `${API_BASE_URL}/api/trigger_niyogin_initiate_digilocker/`,
      opts
    );
    if (res.data?.success === false) {
      throw new Error(res.data?.error || "DigiLocker initiation failed.");
    }
    return res.data;
  };

  // ── Helper: poll the status endpoint ─────────────────────────────────────
  const pollStatus = (): Promise<boolean> =>
    new Promise((resolve) => {
      let attempts = 0;
      const interval = setInterval(async () => {
        attempts += 1;
        setPollAttempt(attempts);
        try {
          const res = await axios.get(`${API_BASE_URL}/api/trigger_niyogin_digilocker_status/`);
          const { has_aadhaar_image, is_verified, kyc_name } = res.data || {};
          if (kyc_name) setKycName(kyc_name);
          if (has_aadhaar_image || is_verified) {
            clearInterval(interval);
            resolve(true);
            return;
          }
        } catch {
          // ignore poll errors — keep trying
        }
        if (attempts >= MAX_POLL_ATTEMPTS) {
          clearInterval(interval);
          resolve(false); // timed out — advance anyway (image may arrive async)
        }
      }, POLL_INTERVAL_MS);
    });

  // ── On return from DigiLocker via URL param: countdown 5s then confirm ────
  useEffect(() => {
    if (!isReturn) return;
    if (calledRef.current) return;

    // Remove the query param from the URL without reloading
    const url = new URL(window.location.href);
    url.searchParams.delete(RETURN_PARAM);
    window.history.replaceState({}, "", url.toString());

    let remaining = 5;
    setCountdown(remaining);

    const tick = setInterval(() => {
      remaining -= 1;
      setCountdown(remaining);
      if (remaining <= 0) {
        clearInterval(tick);
        confirmAndPoll();
      }
    }, 1000);

    return () => clearInterval(tick);
  }, []);

  // ── Confirm KYC + poll for image ──────────────────────────────────────────
  const confirmAndPoll = async () => {
    if (calledRef.current) return;
    calledRef.current = true;

    // Step 1: call confirm API
    setPhase("confirming");
    try {
      const data = await callApi({ digilocker_return: true });
      if (data?.kyc_name) setKycName(data.kyc_name);
    } catch (err: any) {
      const niyoginMsg =
        err?.response?.data?.error ||
        err?.response?.data?.message ||
        (err instanceof Error ? err.message : String(err));
      console.error("[NiyoginInitiateDigilocker] Confirm error:", niyoginMsg, err?.response?.data);
      setError(niyoginMsg);
      setPhase("error");
      return;
    }

    // Step 2: poll until Aadhaar image is saved in DB
    setPhase("polling");
    const imageReady = await pollStatus();

    if (!imageReady) {
      console.warn("[NiyoginInitiateDigilocker] Polling timed out — advancing anyway");
    }

    setPhase("done");
    onDone?.();
  };

  // ── Initial button click ───────────────────────────────────────────────────
  const handleClick = async () => {
    setPhase("calling");
    setError(null);
    try {
      const returnUrl = `${window.location.origin}/loan-application?${RETURN_PARAM}=1`;
      const data = await callApi({ redirect_url: returnUrl });
      const url: string | undefined = data?.digilocker_url;

      if (url) {
        setDigilockerUrl(url);
        // Open DigiLocker in a new tab — user stays on this page
        window.open(url, "_blank", "noopener,noreferrer");
        setPhase("awaiting_user");
      } else {
        // No DigiLocker URL — already verified, advance directly
        setPhase("done");
        onDone?.();
      }
    } catch (err: any) {
      const niyoginMsg =
        err?.response?.data?.error ||
        err?.response?.data?.message ||
        (err instanceof Error ? err.message : String(err));
      console.error("[NiyoginInitiateDigilocker] Error:", niyoginMsg, err?.response?.data);
      setError(niyoginMsg);
      setPhase("error");
    }
  };

  // ── Manual "I've completed KYC" button ────────────────────────────────────
  const handleManualReturn = () => {
    calledRef.current = false;
    confirmAndPoll();
  };

  // ── Render ─────────────────────────────────────────────────────────────────

  // Auto-return countdown
  if (phase === "waiting") {
    return (
      <div className="min-h-screen bg-intellend-white">
        <JourneyHeader />
        <main className="container mx-auto px-4 py-8 max-w-md">
          <div className="bg-white shadow-md rounded-lg p-8 text-center space-y-4">
            <div className="text-5xl">🔄</div>
            <h2 className="text-xl font-bold text-gray-800">Welcome Back!</h2>
            <p className="text-gray-500 text-sm">
              Confirming your DigiLocker KYC in{" "}
              <span className="font-semibold text-intellend-green">{countdown}s</span>…
            </p>
            <div className="flex justify-center">
              <svg className="animate-spin h-8 w-8 text-intellend-green" viewBox="0 0 24 24" fill="none">
                <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
                <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8v8z" />
              </svg>
            </div>
          </div>
        </main>
      </div>
    );
  }

  // Confirming with Niyogin
  if (phase === "confirming") {
    return (
      <div className="min-h-screen bg-intellend-white">
        <JourneyHeader />
        <main className="container mx-auto px-4 py-8 max-w-md">
          <div className="bg-white shadow-md rounded-lg p-8 text-center space-y-4">
            <div className="text-5xl">🔄</div>
            <h2 className="text-xl font-bold text-gray-800">Confirming KYC…</h2>
            <p className="text-gray-500 text-sm">Saving your KYC details with Niyogin, please wait…</p>
            <div className="flex justify-center">
              <svg className="animate-spin h-8 w-8 text-intellend-green" viewBox="0 0 24 24" fill="none">
                <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
                <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8v8z" />
              </svg>
            </div>
          </div>
        </main>
      </div>
    );
  }

  // Polling for Aadhaar image
  if (phase === "polling") {
    return (
      <div className="min-h-screen bg-intellend-white">
        <JourneyHeader />
        <main className="container mx-auto px-4 py-8 max-w-md">
          <div className="bg-white shadow-md rounded-lg p-8 text-center space-y-4">
            <div className="text-5xl">📥</div>
            <h2 className="text-xl font-bold text-gray-800">Saving Aadhaar Data…</h2>
            {kycName && (
              <p className="text-sm text-intellend-green font-medium">✅ Verified: {kycName}</p>
            )}
            <p className="text-gray-500 text-sm">
              Storing your Aadhaar photo and KYC details…
              {pollAttempt > 0 && (
                <span className="text-xs text-gray-400 block mt-1">
                  (attempt {pollAttempt}/{MAX_POLL_ATTEMPTS})
                </span>
              )}
            </p>
            <div className="flex justify-center">
              <svg className="animate-spin h-8 w-8 text-intellend-green" viewBox="0 0 24 24" fill="none">
                <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
                <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8v8z" />
              </svg>
            </div>
          </div>
        </main>
      </div>
    );
  }

  // Awaiting user to finish DigiLocker in the new tab
  if (phase === "awaiting_user") {
    return (
      <div className="min-h-screen bg-intellend-white">
        <JourneyHeader />
        <main className="container mx-auto px-4 py-8 max-w-md">
          <div className="bg-white shadow-md rounded-lg p-8 space-y-6">
            <div className="text-center space-y-2">
              <div className="text-5xl">🪪</div>
              <h2 className="text-xl font-bold text-gray-800">Complete DigiLocker KYC</h2>
              <p className="text-sm text-gray-500">
                DigiLocker has opened in a new tab. Complete your KYC there, then come back here
                and click the button below.
              </p>
            </div>

            {/* Steps */}
            <ol className="text-sm text-gray-600 space-y-2 list-decimal list-inside bg-blue-50 border border-blue-100 rounded-lg p-4">
              <li>Complete the KYC steps in the DigiLocker tab</li>
              <li>Once you see "Documents verified" or a success screen, come back here</li>
              <li>Click <strong>"I've Completed KYC"</strong> below to continue</li>
            </ol>

            {/* Reopen link in case tab was closed */}
            {digilockerUrl && (
              <p className="text-xs text-center text-gray-400">
                Tab closed?{" "}
                <a
                  href={digilockerUrl}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="text-intellend-green underline"
                >
                  Reopen DigiLocker
                </a>
              </p>
            )}

            {/* Primary CTA */}
            <button
              onClick={handleManualReturn}
              className="w-full py-3 px-6 bg-intellend-green text-white font-semibold rounded-lg hover:bg-intellend-green/90 transition-colors"
            >
              ✅ I've Completed KYC — Continue
            </button>

            {/* Secondary: restart */}
            <button
              onClick={() => {
                calledRef.current = false;
                setError(null);
                setDigilockerUrl(null);
                setPhase("idle");
              }}
              className="w-full py-2 px-6 text-sm text-gray-500 underline hover:text-gray-700 transition-colors"
            >
              Start Over
            </button>
          </div>
        </main>
      </div>
    );
  }

  // Error state
  if (phase === "error") {
    return (
      <div className="min-h-screen bg-intellend-white">
        <JourneyHeader />
        <main className="container mx-auto px-4 py-8 max-w-md">
          <div className="bg-white shadow-md rounded-lg p-8 text-center space-y-4">
            <div className="text-5xl">⚠️</div>
            <h2 className="text-xl font-bold text-red-600">DigiLocker KYC Failed</h2>
            <div className="bg-red-50 border border-red-200 rounded-lg p-4 text-left space-y-1">
              <p className="text-sm font-semibold text-red-700">❌ Error from Niyogin</p>
              <p className="text-sm text-red-600">{error}</p>
              <p className="text-xs text-red-500">Please correct the issue and try again.</p>
            </div>
            <button
              className="w-full py-3 px-6 bg-intellend-green text-white font-semibold rounded-lg hover:bg-intellend-green/90 transition-colors"
              onClick={() => {
                calledRef.current = false;
                setError(null);
                setDigilockerUrl(null);
                setPhase("idle");
              }}
            >
              Try Again
            </button>
          </div>
        </main>
      </div>
    );
  }

  // Default: idle / calling — show the action button
  return (
    <div className="min-h-screen bg-intellend-white">
      <JourneyHeader />
      <main className="container mx-auto px-4 py-8 max-w-md">
        <div className="bg-white shadow-md rounded-lg p-8 space-y-6">
          {/* Icon + Title */}
          <div className="text-center space-y-2">
            <div className="text-5xl">🪪</div>
            <h2 className="text-xl font-bold text-gray-800">DigiLocker KYC</h2>
            <p className="text-sm text-gray-500">
              We need to verify your identity via DigiLocker. DigiLocker will open in a new tab —
              complete your KYC there and then return here to continue.
            </p>
          </div>

          {/* Steps */}
          <ol className="text-sm text-gray-600 space-y-2 list-decimal list-inside">
            <li>Click the button below to open DigiLocker</li>
            <li>Log in to DigiLocker and share your Aadhaar</li>
            <li>Return to this tab and click "I've Completed KYC"</li>
          </ol>

          {/* Action Button */}
          <button
            onClick={handleClick}
            disabled={phase === "calling"}
            className="w-full py-3 px-6 bg-intellend-green text-white font-semibold rounded-lg hover:bg-intellend-green/90 disabled:opacity-60 disabled:cursor-not-allowed transition-colors"
          >
            {phase === "calling" ? (
              <span className="flex items-center justify-center gap-2">
                <svg className="animate-spin h-4 w-4" viewBox="0 0 24 24" fill="none">
                  <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
                  <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8v8z" />
                </svg>
                Opening DigiLocker…
              </span>
            ) : (
              "Start DigiLocker KYC →"
            )}
          </button>
        </div>
      </main>
    </div>
  );
};

export default NiyoginInitiateDigilocker;
